import { supabase } from '../supabase';

// Resolve an entity's per-year version for a bill/value year (Q3 policy).
//   table   : 'collectors' | 'assessors'
//   entityId: the stable entity_id (what parcels/bills FK to)
//   year    : the bill's tax_year / the value year to resolve
// Returns { row, flag }:
//   • exact (value_year === year)                → { row, flag: null }
//   • else most recent version AT OR BEFORE year → { row, flag: "No <year> version — using <n>…" }
//   • else earliest available (newer than year)  → { row, flag: "…newer than the bill year…" }
//   • nothing on file                            → { row: null, flag: "No version…" }
// Never silently uses a mismatched-year version — the flag is surfaced to the TS.
export async function resolveVersion(table, entityId, year) {
  if (!entityId || year == null || year === '') return { row: null, flag: null };
  const y = Number(year);
  const base = () => supabase.from(table).select('*').eq('entity_id', entityId);

  const exact = await base().eq('value_year', y).maybeSingle();
  if (exact.error) throw exact.error;
  if (exact.data) return { row: exact.data, flag: null };

  const before = await base().lte('value_year', y).order('value_year', { ascending: false }).limit(1).maybeSingle();
  if (before.error) throw before.error;
  if (before.data) return { row: before.data, flag: `No ${y} version on file — using ${before.data.value_year} (most recent before the bill year). Verify the values.` };

  const earliest = await base().order('value_year', { ascending: true }).limit(1).maybeSingle();
  if (earliest.error) throw earliest.error;
  if (earliest.data) return { row: earliest.data, flag: `No ${y} version on file — using ${earliest.data.value_year} (earliest on file, NEWER than the bill year). Verify the values.` };

  return { row: null, flag: `No version on file for this ${table === 'assessors' ? 'assessor' : 'collector'}.` };
}

// BULK sibling of resolveVersion — the SAME three-tier rule (exact year → latest before → earliest on file),
// resolved for many entities at once. Lives here so the two cannot drift: a screen resolving versions its own
// way would disagree with every other screen on exactly the rows someone opens to check.
//
// ⛔ SET-BASED, not a loop of resolveVersion. That costs up to 3 queries PER ENTITY; this is one paginated
// pass per batch. Batched .in() (100 ids — URL length) with a .range() loop inside each, because the
// versioned tables are ~8x their entity count (collectors alone is ~1816 active rows) and PostgREST caps a
// response at 1000 SILENTLY.
//
// The winner is chosen by comparison, never by row order, so paging cannot pick a stale version.
// Returns Map<entity_id, row>. An entity with NO rows at all is simply absent — callers must handle that
// (a parcel can carry a pre-rebuild assessor_ref_id that matches nothing; see the dangling-refs note).
export async function resolveVersionsBulk(table, entityIds, year) {
  const out = new Map();
  const ids = [...new Set((entityIds || []).filter(Boolean))];
  if (!ids.length || year == null || year === '') return out;
  const y = Number(year);
  if (Number.isNaN(y)) return out;
  const best = new Map();   // entity_id → { exact, before, earliest }
  for (let i = 0; i < ids.length; i += 100) {
    const batch = ids.slice(i, i + 100);
    for (let from = 0; ; from += 1000) {
      const { data, error } = await supabase.from(table).select('*').in('entity_id', batch).range(from, from + 999);
      if (error) throw error;
      (data || []).forEach(r => {
        const vy = Number(r.value_year);
        const b = best.get(r.entity_id) || { exact: null, before: null, earliest: null };
        if (vy === y) b.exact = r;
        if (vy <= y && (!b.before || vy > Number(b.before.value_year))) b.before = r;
        if (!b.earliest || vy < Number(b.earliest.value_year)) b.earliest = r;
        best.set(r.entity_id, b);
      });
      if (!data || data.length < 1000) break;
    }
  }
  best.forEach((b, id) => { const row = b.exact || b.before || b.earliest; if (row) out.set(id, row); });
  return out;
}
